import { RiLoader3Line } from '@remixicon/react'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { Navigate, useParams } from 'react-router'

import { useFlows } from '@/api/flows/flows'
import type { Flow } from '@/api/flows/flows-types'
import { itemsService } from '@/api/items/items-services'
import type { Detail } from '@/api/orders/orders-types'
import { isCompletedDetailStatus } from '@/components/common/selectors/detail-status-selector/config/statuses'
import { DATE_FORMATS } from '@/constants/app'
import { getTargetFlow } from '@/utils/details'

export const getIdStickerLink = (detail: Detail, flows?: Flow[]) => {
  const targetFlow = getTargetFlow({ detail, flows })
  // Every production step ended but status not yet `complete` (ready_for_pack,
  // siding_done…): no machine still lists the item, so it's packaging's turn.
  const allStepsDone = !!flows?.length && !!detail?.production_logs?.length && !targetFlow
  const status = detail?.item?.status

  // Packed/shipped items have no live production log to land on, so the scheduling
  // items tab (the old fallback) filtered them out and the ID-tag QR looked dead.
  // Packaging can show them — it searches history when `ship-date=all`.
  if (
    isCompletedDetailStatus(status) ||
    status === 'packaging' ||
    status === 'ready_for_pack' ||
    allStepsDone
  ) {
    const params = new URLSearchParams({
      search: detail.invoice,
      'ship-date': 'all',
      detail: detail.autoid
    })
    return `/packaging?${params.toString()}`
  }

  // `item.flow` can lag behind finished steps (e.g. still PUNCHTIME after punching is
  // done), landing on a machine whose list no longer holds the item → no sidebar.
  // First unfinished flow is the step the item is actually waiting on; if it has no
  // placed log (e.g. unused FT/VB alternate), keep the old item.flow target.
  const findLog = (inven?: string) => detail?.production_logs?.find((l) => l.flow_inven === inven)
  const targetLog = findLog(targetFlow?.inven)
  const currentLog = targetLog?.stage_id ? targetLog : findLog(detail?.item?.flow?.inven)

  let link = ''

  if (currentLog?.stage_id) {
    link += `/machines/${currentLog.stage_id}/?detail=${detail.autoid}`
  } else {
    link += `/?tab=items&detail=${detail.autoid}&search=${detail.invoice}`
  }

  if (currentLog?.production_date) {
    link += `&date=${format(parseISO(currentLog.production_date), DATE_FORMATS.iso)}`
  }

  return link
}

export const ScannerPage = () => {
  const { detail: detailId } = useParams()

  const {
    data: detail,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['detail', detailId],
    queryFn: () => itemsService.getById(detailId!),
    enabled: !!detailId
  })
  const { data: flows, isLoading: isFlowsLoading } = useFlows()

  if (isLoading || isFlowsLoading)
    return (
      <div className='flex size-full items-center justify-center'>
        <RiLoader3Line className='size-6 animate-spin' />
      </div>
    )

  if (isError)
    return (
      <div className='flex size-full items-center justify-center rounded-md'>
        <div className='rounded-md bg-red-100 p-4 text-lg font-medium text-red-400'>
          {' '}
          Detail {detailId} not found
        </div>
      </div>
    )

  if (!detailId || !detail)
    return (
      <div className='flex size-full items-center justify-center rounded-md bg-red-100 p-4 text-lg text-red-400'>
        Detail not found
      </div>
    )

  const link = getIdStickerLink(detail, flows?.results)

  return (
    <Navigate
      to={link}
      replace
    />
  )
}
